import * as React from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import {
  COUNTRIES,
  PINNED_PHONE_COUNTRIES,
  getCountryName,
  getPhoneCountryCallingCode,
  sanitizeNationalPhoneInput,
  splitPhoneForInput,
} from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { Input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { ScrollArea } from './ScrollArea';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import type { DefaultPhoneCountrySource } from '@/hooks/useDefaultPhoneCountry';

export interface PhoneInputProps {
  id?: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  defaultCountry?: CountryCode;
  defaultCountrySource?: DefaultPhoneCountrySource;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

type PhoneDraft = {
  country: CountryCode;
  nationalDisplay: string;
};

function buildCountryOptions() {
  const pinnedSet = new Set<string>(PINNED_PHONE_COUNTRIES);
  const pinned = PINNED_PHONE_COUNTRIES.map((code) => {
    const row = COUNTRIES.find((c) => c.code === code);
    return row ?? { code, name: getCountryName(code) ?? code };
  });
  const rest = COUNTRIES.filter((c) => !pinnedSet.has(c.code));
  return [...pinned, ...rest];
}

const COUNTRY_OPTIONS = buildCountryOptions();

function computeE164(country: CountryCode, nationalDisplay: string): string {
  const digits = sanitizeNationalPhoneInput(nationalDisplay);
  if (!digits) return '';
  const parsed = parsePhoneNumberFromString(digits, country);
  return parsed?.isValid() ? parsed.format('E.164') : '';
}

export function PhoneInput({
  id,
  value = '',
  onChange,
  onBlur,
  defaultCountry,
  defaultCountrySource,
  disabled,
  error,
  placeholder = 'Phone number',
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: PhoneInputProps) {
  const detectedCountry = useDefaultPhoneCountry(defaultCountrySource);
  const resolvedDefault = defaultCountry ?? detectedCountry;

  const base = useMemo(
    () => splitPhoneForInput(value, resolvedDefault),
    [value, resolvedDefault],
  );

  const [draft, setDraft] = useState<PhoneDraft | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const country = draft?.country ?? base.country;
  const nationalDisplay = draft?.nationalDisplay ?? base.nationalDisplay;

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter((c) => {
      const code = getPhoneCountryCallingCode(c.code as CountryCode).toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        code.includes(q)
      );
    });
  }, [search]);

  const handleCountrySelect = (code: CountryCode) => {
    const nextDraft = { country: code, nationalDisplay };
    setDraft(nextDraft);
    setOpen(false);
    setSearch('');
    onChange(computeE164(code, nationalDisplay));
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setDraft({ country, nationalDisplay: next });
    onChange(computeE164(country, next));
  };

  const handleNationalBlur = () => {
    const digits = sanitizeNationalPhoneInput(nationalDisplay);
    if (digits) {
      const parsed = parsePhoneNumberFromString(digits, country);
      if (parsed) {
        setDraft({ country, nationalDisplay: parsed.formatNational() });
      }
    }
    setDraft(null);
    onBlur?.();
  };

  const callingCode = getPhoneCountryCallingCode(country);
  const invalid = error || ariaInvalid;

  return (
    <div className={cn('flex gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-9 shrink-0 items-center gap-1 rounded-md border border-input bg-background px-2 text-sm shadow-sm transition-colors',
              'focus-visible:outline-none focus-visible:border-primary-500 focus-visible:ring-1 focus-visible:ring-primary-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
              invalid && 'border-destructive focus-visible:ring-destructive',
            )}
            aria-label="Country code"
          >
            <span className="font-medium">{callingCode}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country"
              className="pl-8 h-9"
            />
          </div>
          <ScrollArea className="h-56">
            <ul className="space-y-0.5">
              {filteredCountries.map((c) => {
                const code = c.code as CountryCode;
                const selected = code === country;
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                        selected && 'bg-accent font-medium',
                      )}
                      onClick={() => handleCountrySelect(code)}
                    >
                      <span className="truncate">{c.name}</span>
                      <span className="ml-2 shrink-0 text-muted-foreground">
                        {getPhoneCountryCallingCode(code)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        disabled={disabled}
        value={nationalDisplay}
        onChange={handleNationalChange}
        onBlur={handleNationalBlur}
        placeholder={placeholder}
        aria-invalid={invalid}
        aria-describedby={ariaDescribedBy}
        className={cn('flex-1', invalid && 'border-destructive')}
      />
    </div>
  );
}
